// Individual toast notification with auto-dismiss (paused on hover, focus or
// touch) and a close button. No swipe-to-dismiss.

import { useCallback, useEffect, useState, useRef } from 'react';
import type { Toast as ToastType, ToastType as ToastVariant } from '../../hooks/useToast';

interface ToastProps {
  toast: ToastType;
  onDismiss: (id: string) => void;
}

const icons: Record<ToastVariant, React.ReactNode> = {
  success: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  ),
  error: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  warning: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  ),
  info: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  ),
};

const colorClasses: Record<ToastVariant, { border: string; icon: string; bg: string }> = {
  success: {
    border: 'border-l-lime',
    icon: 'text-lime',
    bg: 'bg-lime/10',
  },
  error: {
    border: 'border-l-coral',
    icon: 'text-coral-strong',
    bg: 'bg-coral/10',
  },
  warning: {
    border: 'border-l-amber',
    icon: 'text-amber',
    bg: 'bg-amber/10',
  },
  info: {
    border: 'border-l-text',
    icon: 'text-text',
    bg: 'bg-text/10',
  },
};

export default function Toast({ toast, onDismiss }: ToastProps) {
  const [isExiting, setIsExiting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const remainingTimeRef = useRef(toast.duration);
  const startTimeRef = useRef(Date.now());

  const handleDismiss = useCallback(() => {
    setIsExiting(true);
    // Wait for exit animation before removing
    setTimeout(() => {
      onDismiss(toast.id);
    }, 200);
  }, [onDismiss, toast.id]);

  useEffect(() => {
    const startTimer = () => {
      startTimeRef.current = Date.now();
      timeoutRef.current = setTimeout(() => {
        handleDismiss();
      }, remainingTimeRef.current);
    };

    const pauseTimer = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        const elapsed = Date.now() - startTimeRef.current;
        remainingTimeRef.current = Math.max(0, remainingTimeRef.current - elapsed);
      }
    };

    if (isPaused) {
      pauseTimer();
    } else {
      startTimer();
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [handleDismiss, isPaused, toast.duration]);

  const colors = colorClasses[toast.type];
  // Only a failure earns the right to interrupt a screen reader mid-sentence
  // (WCAG 4.1.3). role="alert" is the one role assistive tech announces on
  // insertion, which is why it works on a card minted with its text; the rest
  // are announced by the provider's standing live region instead.
  const isError = toast.type === 'error';

  return (
    <div
      className={`
        relative flex items-start gap-3 w-full max-w-sm
        bg-raised/95 backdrop-blur-md
        border border-line border-l-4 ${colors.border}
        rounded-xl shadow-card
        px-4 py-3
        transform transition-all duration-200 ease-out
        ${
          isExiting
            ? 'pointer-events-none opacity-0 translate-y-2 scale-95'
            : 'pointer-events-auto opacity-100 translate-y-0 scale-100'
        }
      `}
      role={isError ? 'alert' : undefined}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocus={() => setIsPaused(true)}
      onBlur={() => setIsPaused(false)}
      // A phone has no hover: holding the card is the only way to keep a toast
      // around long enough to read it.
      onTouchStart={() => setIsPaused(true)}
      onTouchEnd={() => setIsPaused(false)}
      onTouchCancel={() => setIsPaused(false)}
    >
      {/* Icon */}
      <div className={`flex-shrink-0 ${colors.icon}`}>{icons[toast.type]}</div>

      {/* Content */}
      <div className="flex-1 min-w-0 pt-0.5">
        <p className="text-sm text-text leading-snug">{toast.message}</p>
      </div>

      {/* Close button */}
      <button
        onClick={handleDismiss}
        className="flex-shrink-0 p-1 -m-1 text-muted hover:text-text transition-colors rounded-lg"
        aria-label="Dismiss notification"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Progress bar for auto-dismiss */}
      {!isPaused && (
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-line/30 rounded-b-xl overflow-hidden">
          <div
            className={`h-full animate-shrink ${colors.bg.replace('/10', '')}`}
            style={{ animationDuration: `${remainingTimeRef.current}ms` }}
          />
        </div>
      )}
    </div>
  );
}
